/**
 * Toast store — ephemeral notifications, and the record they leave behind.
 *
 * Usage:
 *   import { useToastStore } from '@/lib/toast-store';
 *   const { pushToast } = useToastStore();
 *   pushToast({ kind: 'success', message: 'Resolution signed.' });
 *
 * ── TWO THINGS PAUL ASKED FOR, 25 AUGUST ────────────────────────────────────
 *
 * 1. NOTHING IMPORTANT DISAPPEARS ON A TIMER.
 *
 *    "That notification went and disappeared real quick."
 *
 *    Every toast used to auto-dismiss after four seconds, failures included. On
 *    the walkthrough he invited a team member, the send failed with
 *    ACCOUNT_IN_ANOTHER_WORKSPACE, the toast vanished before he could read it —
 *    and the member row said "on file". He believed the invite had gone out. It
 *    had not, and no email was ever sent.
 *
 *    A four-second window is fine for "Saved." and indefensible for "we could
 *    not send this." `warn` and `error` stay until they are dismissed.
 *    (Re-affirmed by Paul, 22 September, after a round-11 fix had put them on
 *    an 8 s clock. See TOAST_AUTO_DISMISS_MS.)
 *
 * 2. THE BELL HAS TO BE WORTH HAVING.
 *
 *    "Those [toasts] that appear at the bottom right — it should be on the
 *    notification bell, and then I should be able to click on it and view all of
 *    the notifications or dismiss it. Otherwise this bell is unnecessary."
 *
 *    So every toast also writes a `history` entry that outlives it. The bell
 *    renders that beside the server-side notification queue, under its own
 *    heading, because these are two different kinds of fact: the queue is work
 *    the tenant owes, the history is what just happened in this browser. Merging
 *    them silently would make a local UI event look like a server record.
 *
 * History is deliberately IN MEMORY and per-tab. It is a second chance to read
 * something you just missed, not an audit log — the audit log is the audit log,
 * and persisting these to storage would invite them to be mistaken for one.
 */

import { create } from 'zustand';

export type ToastKind = 'success' | 'info' | 'warn' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Optional reference number shown in mono. */
  ref?: string;
}

/** A toast that has been shown, kept so the bell can show it again. */
export interface ToastRecord extends Toast {
  /** Epoch ms, for the relative stamp in the bell. */
  at: number;
  /** Read in the bell — drives the unread count on the badge. */
  seen: boolean;
}

/**
 * How long a toast stays on screen, by kind. One number per kind, changed here
 * and nowhere else. `null` means it stays until the reader dismisses it.
 *
 * A failure is not ephemeral: acting on the belief that something succeeded is
 * the expensive outcome, and no number of seconds is long enough to guarantee
 * a person read a sentence, decided it mattered, and reacted. So a failure has
 * no clock at all.
 *
 * ⚠ DO NOT PUT A CLOCK ON `warn` OR `error`. It was tried on 22 September, as
 * half of the round-11 fix for `NEW-TOAST-BLOCKS-PRIMARY`, and Paul ruled his
 * 25 August instruction stands. The thing that finding actually proved is that
 * a toast must not BLOCK or FOLLOW the reader, which is a different problem
 * with its own three fixes, all of them still in place and none of them a
 * timer:
 *
 *   · the region is `pointerEvents: 'none'` and each toast takes its own, so a
 *     click never lands on the stack instead of the button under it;
 *   · the stack sits bottom-LEFT, away from the right-hand end of a footer
 *     where a screen's primary action lives;
 *   · `clearToasts()` empties it on every route change, so a toast raised on
 *     one screen cannot be sitting on the next one five minutes later.
 *
 * Those three close the finding. A timer would close it by throwing away the
 * sentence instead, which is the failure of 25 August.
 */
export const TOAST_AUTO_DISMISS_MS: Record<ToastKind, number | null> = {
  success: 4000,
  info: 4000,
  warn: null,
  error: null,
};

/** Keep the bell's local list short enough to read at a glance. */
const HISTORY_LIMIT = 30;

interface ToastStore {
  toasts: Toast[];
  /** Newest first. What the bell shows under "On this device". */
  history: ToastRecord[];
  pushToast: (payload: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
  /** Empty the on-screen stack without touching the bell's record. Called on
   *  every route change: a toast is feedback about an action taken on ONE
   *  screen, and carrying it to the next one is how it ends up over a button
   *  it has nothing to do with. */
  clearToasts: () => void;
  /** Mark every history entry read (the bell was opened). */
  markHistorySeen: () => void;
  /** Drop one history entry — Paul's "or dismiss it". */
  dismissHistory: (id: string) => void;
  clearHistory: () => void;
}

let _seq = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  history: [],

  pushToast: (payload) => {
    const id = `toast-${Date.now()}-${++_seq}`;
    const record: ToastRecord = { ...payload, id, at: Date.now(), seen: false };
    set((s) => ({
      toasts: [...s.toasts, { ...payload, id }],
      history: [record, ...s.history].slice(0, HISTORY_LIMIT),
    }));

    const ms = TOAST_AUTO_DISMISS_MS[payload.kind];
    if (ms === null) return; // stays until the reader dismisses it
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, ms);
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  clearToasts: () => set((s) => (s.toasts.length === 0 ? s : { ...s, toasts: [] })),

  markHistorySeen: () =>
    set((s) => ({ history: s.history.map((h) => (h.seen ? h : { ...h, seen: true })) })),

  dismissHistory: (id) => set((s) => ({ history: s.history.filter((h) => h.id !== id) })),

  clearHistory: () => set({ history: [] }),
}));

/** How many history entries have not been read. Drives the bell badge. */
export function unreadToastCount(history: ReadonlyArray<ToastRecord>): number {
  return history.reduce((n, h) => (h.seen ? n : n + 1), 0);
}
